import { Button, Card, Notice, Page, Spinner } from '@/components/ui';
import { getGiftStatus } from '@/lib/api';
import { GIFT_TERM_LABEL, type GiftStatusResponse } from '@shared/types';
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';

/**
 * Post-checkout page for the GIFTER. Paddle redirects here via successUrl.
 * The webhook may land a second or two after the redirect, so we poll until
 * the gift is paid (or give up after ~60s and tell them to check their email).
 */
export function Thanks(): JSX.Element {
  const params = new URLSearchParams(window.location.search);
  const giftId = params.get('g') ?? '';
  const token = params.get('t') ?? '';
  const [gift, setGift] = useState<GiftStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!giftId || !token) {
      setError('This link is missing its reference.');
      return;
    }
    let attempts = 0;
    let timer: number | undefined;
    const poll = async (): Promise<void> => {
      attempts += 1;
      try {
        const g = await getGiftStatus(giftId, token);
        setGift(g);
        if (g.status !== 'pending') return;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load your gift.');
        return;
      }
      if (attempts >= 20) {
        setTimedOut(true);
        return;
      }
      timer = window.setTimeout(poll, 3000);
    };
    void poll();
    return () => window.clearTimeout(timer);
  }, [giftId, token]);

  const copy = async (): Promise<void> => {
    if (!gift?.code) return;
    try {
      await navigator.clipboard.writeText(gift.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <Page>
      <Card className="space-y-4 text-center">
        {error ? (
          <Notice tone="error">{error}</Notice>
        ) : !gift || (gift.status === 'pending' && !timedOut) ? (
          <div className="flex flex-col items-center gap-3 py-6 text-gray-600">
            <Spinner />
            <p>Confirming your payment…</p>
          </div>
        ) : gift.status === 'pending' && timedOut ? (
          <>
            <h1 className="font-heading text-2xl font-bold">Almost there</h1>
            <p className="text-gray-700">
              Your payment is being confirmed. You'll receive an email with the gift code within a few minutes. If it
              doesn't arrive, write to support@betteroo.ai.
            </p>
          </>
        ) : gift.status === 'refunded' || gift.status === 'expired' ? (
          <Notice tone="error">This gift is no longer active.</Notice>
        ) : (
          <>
            <div className="text-5xl">🎁</div>
            <h1 className="font-heading text-2xl font-bold">Thank you!</h1>
            <p className="text-gray-700">
              You gave <strong>{GIFT_TERM_LABEL[gift.term]}</strong> of Betteroo to <strong>{gift.recipientName}</strong>.
            </p>
            {gift.sendToSelf ? (
              <p className="text-gray-700">The gift card is in your inbox. Forward it, print it, or share the code below.</p>
            ) : Date.parse(gift.deliverAt) > Date.now() + 60_000 ? (
              <p className="text-gray-700">
                We'll email {gift.recipientName} on{' '}
                <strong>{new Date(gift.deliverAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}</strong>.
              </p>
            ) : (
              <p className="text-gray-700">We've emailed {gift.recipientName} their gift.</p>
            )}
            {gift.code ? (
              <div className="rounded-xl bg-brand-beige p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">Gift code</p>
                <p className="mt-1 font-mono text-2xl font-bold tracking-wider">{gift.code}</p>
                <button type="button" onClick={copy} className="mt-2 text-sm font-medium text-brand-orange underline">
                  {copied ? 'Copied' : 'Copy code'}
                </button>
              </div>
            ) : null}
            <p className="text-sm text-gray-500">
              A confirmation with the code and your invoice is in your inbox. Paddle, our payment partner, sends the
              receipt separately.
            </p>
            <Button onClick={() => (window.location.href = '/order')} variant="ghost">
              Send another gift
            </Button>
          </>
        )}
      </Card>
    </Page>
  );
}
